import { getQueue } from './offlineQueue';
import { getCompoundQueue } from './offlineCompoundQueue';
import { getImageUploadQueue } from './offlineImageUploadQueue';
import { belongsToCurrentSession, currentQueueOwner, type QueueOwner } from './queueOwner';

export interface QueueCounts {
  /** Reintentables de la sesión actual: se envían solos. */
  pending: number;
  /** Imágenes de la sesión actual en error terminal: botón «Reintentar». */
  failed: number;
  /** De otro usuario o tenant: esperan a que su dueño vuelva a entrar. */
  foreign: number;
}

/**
 * Cuentas del banner (spec 2026-10-01 § 6.3). Lo ajeno va APARTE: si contara
 * como pendiente, el planificador de drenado insistiría cada 15 s → 5 min sin
 * poder enviarlo nunca.
 */
export async function getQueueCounts(): Promise<QueueCounts> {
  const owner = currentQueueOwner();
  const [mutations, compound, images] = await Promise.all([
    getQueue(),
    getCompoundQueue(),
    getImageUploadQueue(),
  ]);
  const own = <T extends { owner?: QueueOwner }>(items: T[]) =>
    items.filter((item) => belongsToCurrentSession(item.owner, owner));
  const ownMutations = own(mutations);
  const ownCompound = own(compound);
  const ownImages = own(images);
  const total = mutations.length + compound.length + images.length;
  const ownTotal = ownMutations.length + ownCompound.length + ownImages.length;
  return {
    pending:
      ownMutations.length +
      ownCompound.length +
      ownImages.filter((op) => op.status !== 'failed').length,
    failed: ownImages.filter((op) => op.status === 'failed').length,
    foreign: total - ownTotal,
  };
}
